import axios from 'axios';
import { clearToken } from './auth';
import { API_BASE_URL, API_ENDPOINTS, DEFAULT_HEADERS, REQUEST_TIMEOUT } from '../config/api.config';
import { getLotSizes as getCachedLotSizes } from './generalDataCache';


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: DEFAULT_HEADERS,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true, // Important: send cookies with requests
});


// Add authentication header to all requests
api.interceptors.request.use(
  (config) => {
    // Token is now sent via httpOnly cookie automatically
    // No need to add Authorization header manually
    // Cookies are sent with withCredentials: true in axios config

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Create a flag to track if we're experiencing API connection issues
    const isNetworkError = !error.response && error.request;
    
    // Set a flag in localStorage to indicate API server status
    if (isNetworkError) {
      localStorage.setItem('api_server_down', 'true');
      console.error('API server connection error:', error.message);
    } else {
      // Clear the flag if we got any kind of response
      localStorage.removeItem('api_server_down');
    }
    
    if (error.response) {
      // Handle unauthorized errors (401)
      if (error.response.status === 401) {
        // Check if this is a Zerodha authentication error (not JWT session expiry)
        const errorMessage = error.response.data?.error || error.response.data?.message || '';
        const isZerodhaAuthError = errorMessage.includes('Authentication failed - please refresh your session');

        if (isZerodhaAuthError) {
          // This is a Zerodha API token expiry - show refresh banner
          localStorage.setItem('zerodha_auth_error', 'true');
          window.dispatchEvent(new Event('zerodha-auth-error'));

          return Promise.reject(new Error('Zerodha session expired. Please refresh your token.'));
        }

        // Check if we should stay logged in (user hasn't explicitly logged out)
        const shouldStayLoggedIn = localStorage.getItem('shouldBeLoggedIn') === 'true';

        if (shouldStayLoggedIn) {
          // This might be a server restart - don't log out automatically

          // If this was the original request (not a retry)
          if (!error.config._isRetry) {
            // Mark as retry to prevent infinite loop
            error.config._isRetry = true;

            // Wait a moment for server to stabilize
            await new Promise(resolve => setTimeout(resolve, 1000));

            try {
              // Try the request again with the same token
              return await api(error.config);
            } catch (retryError) {
              // If retry also fails, continue with normal error handling
            }
          }
        } else {
          // Normal 401 handling - user explicitly logged out or token is invalid
          clearToken();
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(new Error('Session expired. Please login again.'));
      }

      // Handle forbidden errors (403)
      if (error.response.status === 403) {
        return Promise.reject(new Error('You do not have permission to perform this action.'));
      }

      // Handle other errors with response
      const message = error.response.data?.message || 'An error occurred';
      return Promise.reject(new Error(message));
    }

    // Handle network errors
    if (isNetworkError) {
      // IMPORTANT: Do not redirect to login for network errors
      // Just return a rejected promise with an error message
      return Promise.reject(new Error('API server connection error. Please check if the server is running.'));
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

// Centralized function to place orders - can be used by both OptionChain and Positions components
export async function placeOrder(orderData) {
  try {
    
    const response = await api.post(API_ENDPOINTS.PLACE_ORDER, orderData);
    const data = response.data;
    
    // Check for success
    if (!data.success) {
      throw new Error(data.message || 'Order failed');
    }
    
    return data;
  } catch (error) {
    console.error('Error placing order:', error);
    throw error;
  }
};

// Get lot sizes (uses centralized cache)
export const getLotSizes = async () => {
  return await getCachedLotSizes();
};

export default api; 